// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { PrismButton, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import {
  FREQUENCY_DAILY,
  FREQUENCY_TYPE_DAILY,
  FREQUENCY_TYPE_WEEKLY,
  PARITY_REPORT_SUBTYPES,
  REPORT_TYPE_CHOICES,
  TYPE_MARKET_INSIGHT,
  TYPE_MARKETPARITY,
  TYPE_MARKETRATES,
  TYPE_PARITY,
  TYPE_PARITY_INSIGHT,
  TYPE_RATES,
  TYPE_REVENUE_INSIGHT,
} from 'frontend-utils/utils/constants/constant-values';
import GeneralReportScheduleReportScheduleFormSection from 'lumen/components/general/report-schedule/report-schedule-form-section';
import displayRateParityFilters from 'lumen/helpers/display-rate-parity-filters';
import { MEALTYPES } from 'lumen/utils/constants/general-constants';
import { deepExtend } from 'lumen/utils/extend-vanilla-js';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';
import { allSettled, hashSettled } from 'rsvp';

// Constants for reports
const MARKET_LAYOUT = 'market';
const CONFLICT_STRING = 'The ajax operation failed due to a conflict';

const HTML_REPORTS = [TYPE_REVENUE_INSIGHT, TYPE_MARKET_INSIGHT];

interface GeneralReportScheduleReportScheduleSignature {
  Args: {
    brands: unknown;
    canCreateNewUser: unknown;
    canCreateOtherReports: unknown;
    compareType: unknown;
    compareValue: unknown;
    compsetAvailable: unknown;
    compsetId: unknown;
    edit: unknown;
    hotelArg: unknown;
    los: unknown;
    managementTypes: unknown;
    marketArg: unknown;
    maxPersons: unknown;
    mealPlan: unknown;
    membershipType: unknown;
    otaId: unknown;
    passedShopconfigs: unknown;
    rateParam: unknown;
    reportgroup: unknown;
    saveScheduledReports: unknown;
    showMeta: unknown;
    subtype: unknown;
    type: unknown;
  };
}

export default class GeneralReportScheduleReportSchedule extends Component<GeneralReportScheduleReportScheduleSignature> {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Possible error messages after CRUD operations, (All errors if response is not 200 or 409)
  errorMessages = [];
  invalidReportIds = [];
  isLoading = false;
  persons = 2;
  refreshAfterEdit = false;

  // Existing reports
  reportgroups = null;
  // (shopconfig, reportgroup, receivers, weekdays)
  selectedReportInfos = null;

  // Possible success messages
  successMessages = [];
  // Possible warning messages (result of 409 status code)
  warningMessages = [];

  /**
   * Match the available shopconfigs with reportgroups, if there are any available
   * @return {array}   An array consiting of config objects,
   * config objects contain at least a shopconfig. Also a reportgroup if one could be matched
   */
  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'access.isStaffUser',
    'args.reportgroup.frequency',
    'args.{edit,mealPlan,selectedOTA}',
    'los',
    'reportgroups.[]',
    'shopconfigs.[]',
    'subTypes',
    'type',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get configs() {
    // @ts-expect-error: Incorrect type
    const configs = [];
    if (this.type === TYPE_RATES) {
      // For rates simply add a config object for all available shopconfigs
      this.shopconfigs.forEach((shopconfig, index) => {
        // @ts-expect-error: Incorrect type
        const config = this.getConfig(shopconfig, index);
        configs.push(config);
      });
    } else if (this.type === TYPE_PARITY || this.type === TYPE_PARITY_INSIGHT) {
      // Only 1 shopconfig for certain filters
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const shopconfig = this.shopconfigs?.[0];
      // Add a config object for all parity types
      // @ts-expect-error: Incorrect type
      this.subTypes.forEach((subtype, index) => {
        const config = this.getConfig(shopconfig, index, subtype);
        // @ts-expect-error: Incorrect type
        config.subtype = subtype;
        configs.push(config);
      });
    } else if (this.type === TYPE_MARKETRATES) {
      // No shopconfigs available for markets, so create a dummy one
      const shopconfig = {
        shopType: TYPE_MARKETRATES,
        moment: 'morning',
        frequencyType: FREQUENCY_TYPE_DAILY,
        frequency: FREQUENCY_DAILY,
        los: this.los,
        dayOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
        // @ts-expect-error: Incorrect type
        otas: [this.args.selectedOTA],
      };

      // @ts-expect-error: Incorrect type
      const config = this.getConfig(shopconfig, 0);
      configs.push(config);

      // Admins have an additional option, creating market parity reports
      if (
        this.access.isStaffUser &&
        !this.args.edit &&
        this.args.mealPlan === MEALTYPES.ANY
      ) {
        this.createMarketParityShopconfig(configs);
      }
    } else if (this.type === TYPE_MARKETPARITY) {
      // @ts-expect-error: Incorrect type
      this.createMarketParityShopconfig(configs);
    } else if (this.type === TYPE_REVENUE_INSIGHT) {
      if (this.args.edit) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const frequency = this.args.reportgroup?.frequency;
        const frequencyType =
          frequency === FREQUENCY_DAILY ? 'daily' : 'periodic';
        // @ts-expect-error: Incorrect type
        this.createRevenueInsightShopconfig(configs, frequency, frequencyType);
      } else {
        // @ts-expect-error: Incorrect type
        this.createRevenueInsightShopconfig(configs, 0, 'daily');
        // @ts-expect-error: Incorrect type
        this.createRevenueInsightShopconfig(configs, 1, 'periodic');
      }
    } else if (this.type === TYPE_MARKET_INSIGHT) {
      // @ts-expect-error: Incorrect type
      this.createMarketInsightShopConfig(configs);
    }

    return configs;
  }

  @computed('args.reportgroup.hotel.id', 'hotel')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentHotel() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.reportgroup?.hotel?.id
      ? // @ts-expect-error: Incorrect type
        this.args.reportgroup?.hotel
      : this.hotel;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.reportgroup.hotel',
    'args.reportgroup.market.id',
    'currentHotel',
    'market',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentMarket() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.args.reportgroup?.market?.id) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return this.args.reportgroup?.market;
    }
    if (!this.currentHotel) {
      return this.market;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentMarketRatesFilters() {
    const filters = deepExtend({}, this.currentRatesFilters);

    filters.brand = this.args.brands;
    filters.management_type = this.args.managementTypes;
    filters.layout = 'market';
    // Extends rates filters, but can not contain compset.
    // This should be deleted
    delete filters.compset;
    return filters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentParityFilters() {
    const filters = {
      los: this.los,
      persons: this.args.maxPersons,
      mealtype: this.args.mealPlan,
    };
    // @ts-expect-error: Incorrect type
    mapRateParamToAPIParams(this.args.rateParam, filters);
    return filters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentRateChanges() {
    let ratechangeDays;

    if (isComparingRates(this.args.compareType, this.args.compareValue)) {
      if (this.args.compareType === 'extract_date') {
        // rateChanges can be in 2 formats (array in edit dialog) & string in the listView
        if (Array.isArray(this.args.compareValue)) {
          ratechangeDays = this.args.compareValue;
        } else {
          const rateChangeTime = this.hotelAndMarketService.toLocalTime(
            // @ts-expect-error: Incorrect type
            this.args.compareValue,
          );
          const localTime = this.hotelAndMarketService
            .getLocalTime()
            .startOf('day');
          ratechangeDays = [localTime.diff(rateChangeTime, 'day')];
        }
      }
    } else if (this.type !== TYPE_PARITY) {
      ratechangeDays = [1, 3, 7];
    }
    return ratechangeDays;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentRatesFilters() {
    const filters = deepExtend({}, this.currentParityFilters);

    filters.ota = this.args.otaId;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const room = this.args.roomType;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    filters.room = room === 'all' || room.id === 'all' ? '' : room;
    filters.membershiptype = this.args.membershipType;
    if (this.args.compsetAvailable) {
      filters.compset = this.args.compsetId;
    }

    return filters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayFilter() {
    return this.getCurrentFilters(this.type);
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    // CellCta doesn't use the hotelAndMarketService hotel
    return this.args.hotelArg ?? this.hotelAndMarketService.hotel;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotel || this.market;
  }

  @computed('warningMessages.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get invalidUsers() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.warningMessages.map(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      (warningInfo) => warningInfo.report.recipient,
    );
  }

  @computed('selectedReportInfos.{length,[]}', 'invalidReportIds.[]')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isAtLeastOneValidReportSelected() {
    return (
      // @ts-expect-error: Incorrect type
      this.selectedReportInfos?.length >= 1 &&
      this.invalidReportIds?.length === 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get los() {
    return this.args.los ?? 1;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get market() {
    // CellCta doesn't use the hotelAndMarketService market
    return this.args.marketArg ?? this.hotelAndMarketService.market;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.passedShopconfigs.length',
    'args.selectedOTA.{hasMultipleGuestsRequest,supportedMoments}',
    'args.{maxPersons,otaId,type}',
    'hotelOrMarket.{shop1And2Persons,shopconfigs}',
    'los',
    'type',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shopconfigs() {
    if (this.args.passedShopconfigs) {
      // if we have passed shopconfigs,
      // that means this is an edit and all shopconfigs are part of one reportgroup
      // These are orderered by shoplength, so we should only use the last object in the array
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return [
        // @ts-expect-error: Incorrect type
        this.args.passedShopconfigs?.[this.args.passedShopconfigs.length - 1],
      ];
    }

    // Filter the relevant shopconfigs, this is done based on type, ota, los
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const filteredReports = this.hotelOrMarket?.shopconfigs.filter(
      // @ts-expect-error: Incorrect type
      (shop) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        shop.shopType === this.args.type &&
        // Don't filter on OTAs for parity type reports
        (this.type === TYPE_PARITY ||
          this.type === TYPE_PARITY_INSIGHT ||
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          shop.otas.map((ota) => ota.id).indexOf(this.args.otaId) !== -1) &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        shop.los === this.los &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        shop.moment !== 'afternoon' &&
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.args.selectedOTA?.supportedMoments?.indexOf(shop.moment) !== -1 &&
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        (shop.persons.toString() === this.args.maxPersons.toString() ||
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.args.selectedOTA?.hasMultipleGuestsRequest ||
          // @ts-expect-error: Incorrect type
          (this.hotelOrMarket?.shop1And2Persons &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            ['1', '2'].indexOf(shop.persons.toString()) !== -1)),
    );

    // @ts-expect-error: Incorrect type
    let weeklyReports = [];
    // @ts-expect-error: Incorrect type
    let dailyReports = [];

    // Split shopconfigs in daily and weekly ones
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    filteredReports.forEach((shop) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (shop.frequencyType === FREQUENCY_TYPE_WEEKLY) {
        weeklyReports.push(shop);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (shop.frequencyType === FREQUENCY_TYPE_DAILY) {
        dailyReports.push(shop);
      }
    });

    // Reduce the reports, so a single one for each type is left
    if (weeklyReports.length && weeklyReports.length > 1) {
      weeklyReports = [
        // @ts-expect-error: Incorrect type
        weeklyReports.reduce((prev, current) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          prev.shopLength > current.shopLength ? prev : current,
        ),
      ];
    }
    if (dailyReports.length && dailyReports.length > 1) {
      dailyReports = [
        // @ts-expect-error: Incorrect type
        dailyReports.reduce((prev, current) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          prev.shopLength > current.shopLength ? prev : current,
        ),
      ];
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return dailyReports.concat(weeklyReports);
  }

  // Add the subtype for all parity reports (subtype = major otas or other otas)
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subTypes() {
    if (
      this.type === TYPE_PARITY ||
      this.type === TYPE_PARITY_INSIGHT ||
      this.type === TYPE_MARKETRATES ||
      this.type === TYPE_MARKETPARITY
    ) {
      // It is possible we only want to see one subtype, this happens when editing a report
      if (this.args.subtype) {
        return [this.args.subtype];
      }

      if (!this.args.showMeta) {
        return ['major'];
      }

      return PARITY_REPORT_SUBTYPES;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get type() {
    return this.args.type ?? TYPE_RATES;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get user() {
    return this.hotelAndMarketService.user;
  }

  constructor() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    // Initialize arrays
    set(this, 'errorMessages', []);
    set(this, 'warningMessages', []);
    set(this, 'successMessages', []);
    set(this, 'invalidReportIds', []);

    this.didReceiveReportAttrs();
  }

  // @ts-expect-error: Incorrect type
  createMarketInsightShopConfig(configs): void {
    const shopconfig = {
      shopType: TYPE_MARKET_INSIGHT,
      moment: 'morning',
      frequencyType: FREQUENCY_TYPE_WEEKLY,
      frequency: 1, // 1 per week
      dayOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    configs.push(this.getConfig(shopconfig));
  }

  // @ts-expect-error: Incorrect type
  createMarketParityShopconfig(configs): void {
    // Create a dummy parity shopconfigs
    const shopconfig = {
      shopType: TYPE_MARKETPARITY,
      moment: 'morning',
      frequencyType: 'daily',
      frequency: FREQUENCY_DAILY,
      los: this.los,
      dayOfWeek: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
      // @ts-expect-error: Incorrect type
      otas: [this.args.selectedOTA],
    };
    // Add a config object for each subtype
    // @ts-expect-error: Incorrect type
    this.subTypes.forEach((subtype, index) => {
      const config = this.getConfig(shopconfig, index + 1, subtype);
      // @ts-expect-error: Incorrect type
      config.subtype = subtype;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      configs.push(config);
    });
  }

  // @ts-expect-error: Incorrect type
  createRevenueInsightShopconfig(configs, frequency, frequencyType): void {
    const shopconfig = {
      shopType: TYPE_REVENUE_INSIGHT,
      moment: 'morning',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      frequencyType,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      frequency,
      dayOfWeek:
        frequency === FREQUENCY_DAILY
          ? ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
          : ['mon'],
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    configs.push(this.getConfig(shopconfig, frequency));
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  deleteReports(reportsToDelete) {
    const promises = {};
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    reportsToDelete.forEach((report, index) => {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      promises[index] = report.report.destroyRecord();
    });

    return hashSettled(promises).then((hash) => {
      Object.keys(hash).forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const report = reportsToDelete[key];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (hash[key].state === 'rejected') {
          set(this, 'errorMessages', [
            ...this.errorMessages,
            {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              error: hash[key].reason,
              state: 'reportdeletion',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              report,
            },
          ]);
        }
      });
    });
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  editReports(reportsToEdit) {
    const promises = {};
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    reportsToEdit.forEach((report, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const editReport = report.existingReport;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      set(editReport, 'dayOfWeek', report.selectedWeekdays);
      // Only frequency can be edited
      const data = {
        report: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          freq_mon: report.selectedWeekdays.indexOf('mon') !== -1,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          freq_tue: report.selectedWeekdays.indexOf('tue') !== -1,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          freq_wed: report.selectedWeekdays.indexOf('wed') !== -1,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          freq_thu: report.selectedWeekdays.indexOf('thu') !== -1,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          freq_fri: report.selectedWeekdays.indexOf('fri') !== -1,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          freq_sat: report.selectedWeekdays.indexOf('sat') !== -1,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          freq_sun: report.selectedWeekdays.indexOf('sun') !== -1,
        },
      };

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      promises[index] = this.ajax.request(`/reportconfigs/${editReport.id}`, {
        method: 'PUT',
        data: JSON.stringify(data),
        contentType: 'application/json',
      });
    });

    return hashSettled(promises).then((hash) => {
      Object.keys(hash).forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const report = reportsToEdit[key];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (hash[key].state === 'rejected') {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (hash[key].reason.message === CONFLICT_STRING) {
            set(this, 'warningMessages', [
              ...this.warningMessages,
              {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                error: hash[key].reason,
                state: 'reportedit',
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                report,
              },
            ]);
          } else {
            set(this, 'errorMessages', [
              ...this.errorMessages,
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              { error: hash[key].reason, state: 'reportedit', report },
            ]);
          }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          set(this, 'successMessages', [...this.successMessages, { report }]);
        }
      });
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  fetchReports() {
    set(this, 'reportgroups', []);
    set(this, 'isLoading', true);

    const data = {
      my_reports: true,
    };

    if (this.hotel) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      data.hotel_id = this.hotel?.id;
    } else if (this.market) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      data.market_id = this.market?.id;
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.store
      .query('reportgroup', {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        hotel_id: this.hotel?.id,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        market_id: this.market?.id,
        page: 0,
        my_reports: true,
      })
      .then((reportgroups) => {
        // Filter relevant reports
        const filteredReportgroups = reportgroups.filter((group) => {
          // Has to be the same type
          const hasMatchingType =
            // @ts-expect-error: Incorrect type
            group.type === REPORT_TYPE_CHOICES[this.type] ||
            (this.type === TYPE_MARKETRATES &&
              // @ts-expect-error: Incorrect type
              group.type === REPORT_TYPE_CHOICES[TYPE_MARKETPARITY]);
          // Hotel/Market id has to match (except in hotel settings but this code isn't executed for the settings route, see didReceiveAttrs)
          const hasMatchingId =
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            group.hotel?.id === this.hotelOrMarket.id ||
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            group.market?.id === this.hotelOrMarket.id;
          if (!hasMatchingType || !hasMatchingId) {
            return false;
          }

          // Afterwards check if the filters match
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const groupFilters = group.filters;
          // @ts-expect-error: Incorrect type
          let currentFilters = this.getCurrentFilters(group.type);

          // Since we need the frequency we need to add the ratechange_days dynamically
          // @ts-expect-error: Incorrect type
          if (this.shouldAddRateChanges(group.frequency, group.type)) {
            currentFilters = {
              ...currentFilters,
              ratechange_days: this.currentRateChanges,
            };
          }

          let isSame = true;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Object.keys(groupFilters).forEach((key) => {
            // if a key is present one one, but not the other, filters are not the same
            const isEitherKeysPresent =
              // @ts-expect-error: Incorrect type
              key in groupFilters || key in currentFilters;
            // if both keys are null, undefined, an empty string, or any combination, they are treated as equal
            const areBothKeysEmpty =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              isEmpty(currentFilters[key]) && isEmpty(groupFilters[key]);
            // Some keys are int, some are strings, others are arrays. With a string check it's possible to easilty check them all
            const areBothKeysEqual =
              // @ts-expect-error: Incorrect type
              !isEmpty(currentFilters[key]) &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !isEmpty(groupFilters[key]) &&
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              currentFilters[key].toString() === groupFilters[key].toString();

            // Only do these if's when isSame is still true (some checks can change isSame back to true, which we don't want)
            isSame =
              isEitherKeysPresent &&
              (areBothKeysEmpty || areBothKeysEqual) &&
              isSame;
            if (!isSame) {
              if (key === 'compset') {
                // if compset is present in the filters, but the hotel does not have this feature, we should still treat the filters as equal
                // Frontend filters only include the compset if the feature is available, but the API always includes it
                isSame = !this.args.compsetAvailable;
              } else if (key === 'subtype') {
                isSame = true;
              }
            }
          });

          return isSame;
        });

        set(this, 'reportgroups', [
          // @ts-expect-error: Incorrect type
          ...this.reportgroups,
          ...filteredReportgroups,
        ]);
        set(this, 'isLoading', false);
      });
  }

  /**
   * Put report (in the selectedREportInfos array) in the create, update, delete or new user array
   * @param  {array} reportsToCreate    :
   * Array with info to create a report
   * @param  {array} reportsToEdit      :
   * Array with info to edit a report and the existing report
   * @param  {array} reportsToDelete    :
   * Array of existing reports, which need to be deleted
   */

  filterReports(
    // @ts-expect-error: Incorrect type
    reportsToBeCreated,
    // @ts-expect-error: Incorrect type
    reportsToBeEdited,
    // @ts-expect-error: Incorrect type
    reportsToBeDeleted,
  ): void {
    /*
      selectedShopconfig contains:
      - config: shopconfig
      - reportgroup
      - selectedRecipients: array of selected recipients
      - selectedWeekdays: array of selected weekdays
    */
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.selectedReportInfos
      .filter(
        // @ts-expect-error: Incorrect type
        (reportInfo) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          !reportInfo.reportgroup ||
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          reportInfo.hasReportgroupChanged ||
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          !reportInfo.isExpanded,
      )
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .forEach((config) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const receivers = config.selectedRecipients;
        // For all these receivers the report should be created or updated
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        receivers.forEach((receiver) => {
          const report = {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            shopconfig: config.config,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            reportgroup: config.reportgroup,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            recipient: receiver,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            selectedWeekdays: config.selectedWeekdays,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            subtype: config.subtype,
          };

          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            receiver.id &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            config.reportgroup &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            config.reportgroup.receivers.find(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (reportGroupReceiver) => reportGroupReceiver.id === receiver.id,
            )
          ) {
            // A report is only editable if the receiver is already in the report group
            // and the day of week array does not match
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const existingReport = config.reportgroup.reports.find(
              // @ts-expect-error: Incorrect type
              (reportGroupReport) =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                reportGroupReport.receiver.id === receiver.id,
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const reportDayOfWeek = existingReport.dayOfWeek.sort().join(',');
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const dayOfWeek = config.selectedWeekdays.sort().join(',');
            if (reportDayOfWeek !== dayOfWeek) {
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              report.existingReport = existingReport;
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              reportsToBeEdited.push(report);
            }
          } else {
            // otherwise it has to be created
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            reportsToBeCreated.push(report);
          }
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (config.reportgroup) {
          // If the receiver is not in the selected receivers,
          // but it is in the reportgroup, it needs to be deleted
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          config.reportgroup.reports.forEach((report) => {
            if (
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              !config.isExpanded ||
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              !receivers.find((receiver) => receiver.id === report.receiver?.id)
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              reportsToBeDeleted.push({
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                report,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                deleteAll: !config.isExpanded,
              });
            }
          });
        }
      });
  }

  /**
   * Check if the shopconfig is the correct type
   * and if there is a reportgroup that matches the shopconfig
   * @param  {object} shopconfig : shopconfig to check
   * @param  {int}    index      : used to create an id
   * @param  {string}  subtype   : subtype for parity reports
   * @return {object}            : return an object with all info to create/edit a report
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getConfig(shopconfig, index, subtype) {
    // Check the type, an exception is made for markets since more than 1 type can be present there
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      shopconfig.shopType !== this.type &&
      !(
        this.type === TYPE_MARKETRATES &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        shopconfig.shopType === TYPE_MARKETPARITY
      )
    ) {
      return null;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const config = { shopconfig };

    // Filter out relevant reportgroups
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const reportgroups = this.reportgroups.filter((group) => {
      // First of all, check if the report is of the correct subtype if one is given
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (subtype && group.reports?.[0]?.filters?.subtype !== subtype) {
        return false;
      }

      // If there is no id and it's a market report
      // we can always return true since there are no shopconfigs for those types
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!shopconfig.id) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (group.type === REPORT_TYPE_CHOICES[TYPE_MARKETRATES]) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return shopconfig.shopType === TYPE_MARKETRATES;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (group.type === REPORT_TYPE_CHOICES[TYPE_MARKETPARITY]) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return shopconfig.shopType === TYPE_MARKETPARITY;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (group.type === REPORT_TYPE_CHOICES[TYPE_REVENUE_INSIGHT]) {
          return (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            shopconfig.shopType === TYPE_REVENUE_INSIGHT &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            group.frequency === shopconfig.frequency
          );
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (group.type === REPORT_TYPE_CHOICES[TYPE_MARKET_INSIGHT]) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return shopconfig.shopType === TYPE_MARKET_INSIGHT;
        }
      }

      // Otherwise we try to find shopconfigs whose ids match those in the reportgroups
      // @ts-expect-error: Incorrect type
      let ids = [];
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      group.shopconfigs.forEach((groupConfig) => {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        ids = ids.concat(groupConfig.id.split(','));
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const matchIds = shopconfig.id.split(',');
      // Check if any of the shopconfigs attachted to the reportgroup
      // match the current (aggregated) shopconfig
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return matchIds.some((id) => ids.indexOf(id) !== -1);
    });

    // If there is more than 1, fetch the last one
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (reportgroups.length) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      config.reportgroup = reportgroups.sort(
        // @ts-expect-error: Incorrect type
        (a, b) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          a.lastEditedOn.isBefore(b.lastEditedOn) ? 1 : -1,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      )[0];
    }

    // Create an id
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    config.id = [shopconfig.id, index].join(',');

    return config;
  }

  /**
   * Get the filters for the passed type
   * @param  {string} type : type of the shopconfig
   * @return {object}      : filters (ota,los...)
   */
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCurrentFilters(type) {
    if (type === TYPE_RATES || type === REPORT_TYPE_CHOICES[TYPE_RATES]) {
      return this.currentRatesFilters;
    }
    if (
      type === TYPE_PARITY ||
      type === REPORT_TYPE_CHOICES[TYPE_PARITY] ||
      type === TYPE_MARKETPARITY ||
      type === REPORT_TYPE_CHOICES[TYPE_MARKETPARITY] ||
      type === TYPE_PARITY_INSIGHT ||
      type === REPORT_TYPE_CHOICES[TYPE_PARITY_INSIGHT]
    ) {
      return this.currentParityFilters;
    }
    if (
      type === TYPE_MARKETRATES ||
      type === REPORT_TYPE_CHOICES[TYPE_MARKETRATES]
    ) {
      return this.currentMarketRatesFilters;
    }
    if (
      type === TYPE_REVENUE_INSIGHT ||
      type === REPORT_TYPE_CHOICES[TYPE_REVENUE_INSIGHT]
    ) {
      return {};
    }
    return null;
  }

  /**
   * Function to handle the flow of handling editing reports
   * and continuing to saveUpdateDeleteReports
   * @param  {array} reportsToCreate    :
   * Array with info to create a report
   * @param  {array} reportsToEdit      :
   * Array with info to edit a report and the existing report
   * @param  {array} reportsToDelete    : Array of existing reports, which need to be deleted
   */
  async handleEditReports(
    // @ts-expect-error: Incorrect type
    reportsToBeCreated,
    // @ts-expect-error: Incorrect type
    reportsToBeEdited,
    // @ts-expect-error: Incorrect type
    reportsToBeDeleted,
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    let editableReportsToBeEdited = reportsToBeEdited;
    // Check if there is a report that needs to be edited
    // If there is, then check to see if it should be changed for all users
    // or only for the current users
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (editableReportsToBeEdited.length > 1) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const ownReports = editableReportsToBeEdited.filter(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        (report) => report.reportgroup && report.recipient.id === this.user?.id,
      );

      // Should only be used if you have reports for yourself and for others
      if (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ownReports.length &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ownReports.length !== editableReportsToBeEdited.length
      ) {
        const confirmWasClicked = await this.alerting.showWarningAlert({
          title: this.intl.t('settings.reports.edit_report'),
          text: this.intl.t('reports.edit_for_everyone'),
          buttons: {
            cancel: {
              text: this.intl.t('reports.edit_for_everyone_cancel'),
            },
            confirm: {
              text: this.intl.t('reports.edit_for_everyone_confirm'),
            },
          },
        });

        if (!confirmWasClicked) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          editableReportsToBeEdited = [...ownReports];
        }
      }
    }

    this.saveUpdateDeleteReports(
      reportsToBeCreated,
      editableReportsToBeEdited,
      reportsToBeDeleted,
    );
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  saveReports(reportsToBeCreated) {
    const promises = {};
    // Create a report with the info from the form and the shopconfig, has to be custom for the api
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    reportsToBeCreated.forEach((report, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const { frequencyType, frequency, moment, shopType } = report.shopconfig;

      const data = deepExtend(
        {},
        {
          report: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            freq_mon: report.selectedWeekdays.indexOf('mon') !== -1,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            freq_tue: report.selectedWeekdays.indexOf('tue') !== -1,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            freq_wed: report.selectedWeekdays.indexOf('wed') !== -1,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            freq_thu: report.selectedWeekdays.indexOf('thu') !== -1,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            freq_fri: report.selectedWeekdays.indexOf('fri') !== -1,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            freq_sat: report.selectedWeekdays.indexOf('sat') !== -1,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            freq_sun: report.selectedWeekdays.indexOf('sun') !== -1,
            filters: this.getCurrentFilters(shopType),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            receiver: report.recipient.id,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            report_type: REPORT_TYPE_CHOICES[shopType],
            // Frequency is daily for a PI report (limited parity) since we don't know which day it needs to get sent
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            frequency:
              frequencyType === FREQUENCY_TYPE_DAILY ||
              this.type === TYPE_PARITY_INSIGHT
                ? FREQUENCY_DAILY
                : frequency,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            moment,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            delivery_type: HTML_REPORTS.includes(shopType) ? 'html' : 'excel',
          },
        },
      );

      // @ts-expect-error: Incorrect type
      if (this.hotel?.id) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data.report.hotel = this.hotel?.id;
        // @ts-expect-error: Incorrect type
      } else if (this.market?.id) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        data.report.market = this.market?.id;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        data.report.filters.layout = MARKET_LAYOUT;
      }

      if (
        this.shouldAddRateChanges(
          // @ts-expect-error: Incorrect type
          data.report.frequency,
          // @ts-expect-error: Incorrect type
          data.report.report_type,
        )
      ) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        data.report.filters.ratechange_days = this.currentRateChanges;
      }
      // Add the subtype for all parity reports (subtype = major otas or other otas)
      if (
        (shopType === TYPE_PARITY ||
          shopType === TYPE_MARKETPARITY ||
          shopType === TYPE_PARITY_INSIGHT) &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        report.subtype
      ) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        data.report.filters.subtype = report.subtype;
      }

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      promises[index] = this.ajax.request('/reportconfigs/', {
        method: 'POST',
        data: JSON.stringify(data),
        contentType: 'application/json',
      });
    });
    // Use hash settled to see which reports could be created and which ones could not
    return hashSettled(promises).then((hash) => {
      Object.keys(hash).forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const report = reportsToBeCreated[key];
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (hash[key].state === 'rejected') {
          this.errorTracking.captureMessage('Report schedule failed', {
            // @ts-expect-error: Incorrect type
            hashKey: hash[key],
          });
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (hash[key].reason.message === CONFLICT_STRING) {
            set(this, 'warningMessages', [
              ...this.warningMessages,
              {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                error: hash[key].reason,
                state: 'reportcreation',
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                report,
              },
            ]);
          } else {
            set(this, 'errorMessages', [
              ...this.errorMessages,
              {
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                error: hash[key].reason,
                state: 'reportcreation',
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                report,
              },
            ]);
          }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          set(this, 'successMessages', [...this.successMessages, { report }]);
        }
      });
    });
  }

  /**
   * Handle updating deletings and saving, perform a refresh after when necessary
   * @param  {array} reportsToCreate    : Array with info to create a report
   * @param  {array} reportsToEdit      : Array with info to edit a report and the existing report
   * @param  {array} reportsToDelete    : Araay of existing reports, which need to be deleted
   */
  saveUpdateDeleteReports(
    // @ts-expect-error: Incorrect type
    reportsToCreate,
    // @ts-expect-error: Incorrect type
    reportsToEdit,
    // @ts-expect-error: Incorrect type
    reportsToDelete,
  ): void {
    const promises = [
      this.saveReports(reportsToCreate),
      this.editReports(reportsToEdit),
      this.deleteReports(reportsToDelete),
    ];

    allSettled(promises).then(() => {
      set(this, 'isLoading', false);

      if (this.errorMessages.length) {
        this.errorTracking.captureMessage(
          'Failed to save/update/delete reports',
          {
            errorMessages: this.errorMessages,
          },
        );
      }

      // @ts-expect-error: Incorrect type
      this.args.saveScheduledReports(
        this.errorMessages,
        this.warningMessages,
        this.successMessages,
      );

      if (this.refreshAfterEdit) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.doRefresh();
      }
    });
  }

  // Report info is an object containing all info needed to create a report
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  shouldAddRateChanges(frequency, type) {
    return (
      frequency === FREQUENCY_DAILY &&
      (type === REPORT_TYPE_CHOICES[TYPE_RATES] ||
        type === REPORT_TYPE_CHOICES[TYPE_MARKETRATES]) &&
      this.currentRateChanges
    );
  }

  @action
  didReceiveReportAttrs(): void {
    // Reset arrays
    set(this, 'selectedReportInfos', []);

    // Check if reportgroup was passed,
    // this will happen in the reports overview, when editing a report
    if (this.args.reportgroup) {
      set(this, 'reportgroups', [this.args.reportgroup]);
    }

    // Fetch reports if we do not want to edit an existing one
    if (!this.reportgroups) {
      this.fetchReports();
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  reportSectionChanged(isValid, data, reportInfoId, isExpanded): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const config = this.selectedReportInfos.find(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (info) => info.id === reportInfoId,
    );
    if (config) {
      // If it already exists, remove it and then add it again
      set(
        this,
        'selectedReportInfos',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.selectedReportInfos.filter((info) => info !== config),
      );
    }
    if (isValid) {
      // @ts-expect-error: Incorrect type
      set(this, 'selectedReportInfos', [...this.selectedReportInfos, data]);
    }

    if (isValid || (!isValid && !isExpanded)) {
      set(
        this,
        'invalidReportIds',
        this.invalidReportIds.filter((id) => id !== reportInfoId),
      );
    } else {
      set(this, 'invalidReportIds', [...this.invalidReportIds, reportInfoId]);
    }
  }

  @action
  async scheduleNewReport(): Promise<void> {
    // Reset error, warning and succes messages
    set(this, 'errorMessages', []);
    set(this, 'warningMessages', []);
    set(this, 'successMessages', []);
    set(this, 'invalidReportIds', []);
    // Existing users, and newly created users should be put in one of these collections
    // @ts-expect-error: Incorrect type
    const reportsToBeEdited = [];
    // @ts-expect-error: Incorrect type
    const reportsToBeCreated = [];
    // @ts-expect-error: Incorrect type
    let reportsToBeDeleted = [];

    set(this, 'isLoading', true);

    this.filterReports(
      // @ts-expect-error: Incorrect type
      reportsToBeCreated,
      // @ts-expect-error: Incorrect type
      reportsToBeEdited,
      // @ts-expect-error: Incorrect type
      reportsToBeDeleted,
    );

    if (reportsToBeCreated.length) {
      // @ts-expect-error: Incorrect type
      reportsToBeCreated.forEach((report) => {
        this.usageAnalytics.track('Report Scheduled', {
          eventTrigger: 'report-schedule',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          reportType: report.shopconfig.frequencyType,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          daysOfWeek: report.selectedWeekdays.join(', '),
        });
      });
    }

    // Check to see if any can be deleted
    const checkSelfDelete =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      reportsToBeDeleted.filter((report) => report.deleteAll).length > 1;

    if (checkSelfDelete) {
      // @ts-expect-error: Incorrect type
      const deleteReportsForMyself = reportsToBeDeleted.filter(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (report) => report.report.receiver?.id === this.user?.id,
      );

      if (
        deleteReportsForMyself.length &&
        deleteReportsForMyself.length !== reportsToBeDeleted.length
      ) {
        // @ts-expect-error: Incorrect type
        const ownEditReports = reportsToBeEdited.filter(
          (report) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            report.reportgroup && report.recipient.id === this.user?.id,
        );

        const closeModal = !(
          reportsToBeEdited.length > 1 &&
          ownEditReports.length &&
          ownEditReports.length !== reportsToBeEdited.length
        );

        const confirmWasClicked = await this.alerting.showWarningAlert({
          title: this.intl.t('settings.reports.delete_report'),
          text: `${this.intl.t('reports.delete_reports_creator')}?`,
          buttons: {
            cancel: {
              text: this.intl.t('reports.delete_reports_creator_self_only'),
            },
            confirm: {
              text: this.intl.t('general.yes'),
            },
          },
          // @ts-expect-error: Incorrect type
          unsafe_buttonClickCanCloseAlert: closeModal,
        });

        if (!confirmWasClicked) {
          reportsToBeDeleted = [deleteReportsForMyself];
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.handleEditReports(
      // @ts-expect-error: Incorrect type
      reportsToBeCreated,
      // @ts-expect-error: Incorrect type
      reportsToBeEdited,
      // @ts-expect-error: Incorrect type
      reportsToBeDeleted,
    );
  }

  <template>
    <div
      class="modal--medium"
      {{didUpdate this.didReceiveReportAttrs @reportgroup}}
    >
      <div class="filter-overview relative">
        <span
          class="filter-overview-values"
          data-test-id="day-detail-filter-breadcrumbs"
        >
          {{#if this.hotel}}
            {{! @glint-expect-error: Incorrect type }}
            {{displayRateParityFilters this.displayFilter this.hotel}}
          {{else}}
            {{! @glint-expect-error: Incorrect type }}
            {{displayRateParityFilters this.displayFilter this.market}}
          {{/if}}
        </span>
      </div>
      <div class="schedule-reports-form-content relative">
        {{#if this.isLoading}}
          <PrismLoadingBar />
        {{/if}}
        <span class="text-300--regular">
          {{t "reports.schedule_type_question"}}:
        </span>
        <div class="reports-form-wrapper mt-300">
          {{#each this.configs as |config|}}
            {{! @glint-expect-error: Incorrect type }}
            <GeneralReportScheduleReportScheduleFormSection
              @canCreateNewUser={{@canCreateNewUser}}
              @canCreateOtherReports={{@canCreateOtherReports}}
              @currentHotel={{this.currentHotel}}
              @currentMarket={{this.currentMarket}}
              @currentRateChanges={{this.currentRateChanges}}
              @edit={{@edit}}
              {{! @glint-expect-error: Incorrect type }}
              @id={{config.id}}
              @invalidUsers={{this.invalidUsers}}
              @isLoading={{this.isLoading}}
              @maxPersons={{@maxPersons}}
              @reportSectionChanged={{this.reportSectionChanged}}
              {{! @glint-expect-error: Incorrect type }}
              @reportgroup={{config.reportgroup}}
              @shopconfig={{config.shopconfig}}
              {{! @glint-expect-error: Incorrect type }}
              @subtype={{config.subtype}}
              @user={{this.user}}
            />
          {{/each}}
        </div>
      </div>
      <div class="reports-form-footer flex justify-center">
        <PrismButton
          @isDisabled={{not this.isAtLeastOneValidReportSelected}}
          @label={{t "general.save"}}
          @onClick={{this.scheduleNewReport}}
        />
      </div>
    </div>
  </template>
}
